import {compact, concat} from 'lodash';
import React from 'react';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {createSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {buildTagSearchUrl} from '../../../../../helpers/models/tagHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {ApiSearchTags, SearchTagModel} from '../../../../../models/api/searchTagModel';
import {TagBehaviorsEnum} from '../../../../../models/api/tagModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {AccountDropdownItem} from '../../../../common/dropdowns/accountDropdownItem';
import {TagDropdownItem} from '../../../../common/dropdowns/tagDropdownItem';
import {getTagAliasDuplicates} from '../../../composer/toolbar/dropdowns/composerTagsDropdown';
import {InputValue, SyncDropdownInputProps} from './inboxesSearchQueryBuilderTypes';

/*
 * Helpers.
 */

function replaceDoubleQuotes(value: string) {
  return value.replace(/"/g, '');
}

export function maybeWrapWithDoubleQuotes(value: string) {
  if (/\s/.test(value)) {
    // Remove quotes added in the input as they would mess up with `SearchToken` value.
    return `"${replaceDoubleQuotes(value)}"`;
  }

  return replaceDoubleQuotes(value);
}

export function maybeRemoveDoubleQuotes(value: string | undefined) {
  if (!value) {
    return '';
  }

  const [, unquotedValue] = value.match(/^"(.*)"$/) || [];

  return unquotedValue || value;
}

/*
 * Tags.
 */

export const getFetchTagsAsync = createSelector(
  [getCompany],
  (company) =>
    async function fetchAsync(query: string, tags: ReadonlyArray<SearchTagModel>, cancelToken: CancelToken) {
      const url = buildTagSearchUrl(company, query, {
        offset: tags.length,
        behaviors: [TagBehaviorsEnum.CAN_PARENT],
      });

      const newTags = await fetchApiAsync(ApiSearchTags, url, {cancelToken});
      return {
        items: concat(tags.length > 0 ? tags : [], newTags.tags),
        canLoadMore: newTags.tags.length === newTags.limit,
      };
    },
);

export function makeRenderTagRow(selectedTagAlias: InputValue | undefined, onSelect: HandlerOf<InputValue>) {
  return function (
    searchTag: SearchTagModel,
    index: number,
    searchTags: ReadonlyArray<SearchTagModel | null>,
    isSearching?: boolean,
  ) {
    const tag = searchTag[0];
    const tagAliasDuplicates = getTagAliasDuplicates(searchTags);

    const nestingLevel = isSearching ? 0 : searchTag.length - 1;
    const tagsPath = isSearching ? searchTag : [];

    return (
      <TagDropdownItem
        tag={tag}
        tagsPath={tagsPath}
        key={tag.id}
        index={index}
        isChecked={tag.alias === selectedTagAlias?.value}
        nestingLevel={nestingLevel}
        shouldShowDeduplication={tagAliasDuplicates.has(tag.alias)}
        onSelect={(e) => onSelect({modifier: ModifiersEnum.TAG, value: tag.alias})}
      />
    );
  };
}

export function makeRenderAccountRow(
  selectedAccountName: InputValue | undefined,
  onSelect: HandlerOf<InputValue>,
) {
  return function (searchAccount: AccountModel) {
    const isChecked = selectedAccountName?.value === searchAccount.name;

    // alias values should always be set, but fallback to name if not provided.
    const value = searchAccount.alias ?? searchAccount.name;

    return (
      <AccountDropdownItem
        key={searchAccount.id}
        account={searchAccount}
        isChecked={isChecked}
        onSelect={(e) => onSelect({modifier: ModifiersEnum.ACCOUNT, value})}
      />
    );
  };
}

/*
 * Status.
 */

export const statusDropdownItems = (): ReadonlyArray<SyncDropdownInputProps> =>
  compact([
    {
      icon: 'assignSmall',
      inputValue: {value: 'assigned'},
      label: <Trans id="search-query-builder-status-assigned">Assigned</Trans>,
    },
    {
      icon: 'archiveSmall',
      inputValue: {value: 'archived'},
      label: <Trans id="search-query-builder-status-archived">Archived</Trans>,
    },
    {
      icon: 'discussionSmall',
      inputValue: {value: 'discussion'},
      label: <Trans id="search-query-builder-status-discussion">Discussion</Trans>,
    },
    {
      icon: 'muteSmall',
      inputValue: {value: 'muted'},
      label: <Trans id="search-query-builder-status-unsubscribed">Unsubscribed</Trans>,
    },
    {
      icon: 'inboxSmall',
      inputValue: {value: 'open'},
      label: <Trans id="search-query-builder-status-open">Open</Trans>,
    },
    {
      icon: 'teammatesSmall',
      inputValue: {value: 'shared-with-me'},
      label: <Trans id="search-query-builder-status-shared-with-me">Shared with me</Trans>,
    },
    {
      icon: 'snoozeSmall',
      inputValue: {value: 'snoozed'},
      label: <Trans id="search-query-builder-status-snoozed">Snoozed</Trans>,
    },
    {
      icon: 'spamSmall',
      inputValue: {value: 'spam'},
      label: <Trans id="search-query-builder-status-spam">Spam</Trans>,
    },
    {
      icon: 'starSmall',
      inputValue: {value: 'starred'},
      label: <Trans id="search-query-builder-status-starred">Starred</Trans>,
    },
    {
      icon: 'subscribeSmall',
      inputValue: {value: 'subscribed'},
      label: <Trans id="search-query-builder-status-subscribed">Subscribed</Trans>,
    },
    {
      icon: 'trashSmall',
      inputValue: {value: 'trashed'},
      label: <Trans id="search-query-builder-status-trashed">Trashed</Trans>,
    },
    {
      icon: 'unassignedSmall',
      inputValue: {value: 'unassigned'},
      label: <Trans id="search-query-builder-status-unassigned">Unassigned</Trans>,
    },
    {
      icon: 'eyeCrossedSmall',
      inputValue: {value: 'unread'},
      label: <Trans id="search-query-builder-status-unread">Unread</Trans>,
    },
    {
      icon: 'repliedSmall',
      inputValue: {value: 'unreplied'},
      label: <Trans id="search-query-builder-status-unreplied">Unreplied</Trans>,
    },
  ]);
